import {
  map,
  pathOr,
  split,
  last,
  dropLast,
  set,
  lensPath,
  findIndex,
  propEq,
  append,
  view,
  equals,
  head,
  keys,
  flatten,
} from 'ramda';
import { compose } from 'recompose';
import { isIndexIsTrue } from 'src/utilities/isIndexIsTrue';
import { checkAndGetAnswerFrmUserInput, isMapWithSameKey } from 'src/features/core/ChatEngine/Bot/botServiceUtils/commonFns';

//////////////////  PUBLIC FUNCTIONS ////////////////////////

/**
 * mkAnswerAsObjectForPath - method which makes the object to be attached with consents
 * array in declarations resources ,for eg: {name:"l","value":"<user inpt>"}
 * @param dataBag - consists of required datas inside it which intiated in SimpleBotService File
 * @returns updated new databag which will be used by its next functions,where two constant
 * will be addeded, i.e. arrayOfAnswerObject = [{
 * path:['<path to the ARRAY in API'],
 * answerObject:{"name":"l","value":"yes"},
 * uniqMapKey:"O"
 * }] and answerObject={"name":"l","value":"yes"}
 */
export const mkAnswerAsObjectForPath = (dataBag: any) => {
  const { mappingKeys } = dataBag;
  let _dataBag = dataBag;
  const mappedForFn = (mappingKey: any) =>
    (_dataBag = mkSaveInApiDataForArray(mappingKey, _dataBag));
  map(mappedForFn, mappingKeys);
  return _dataBag;
};

/**
 * mkResourceWithAnswerObject - This method maps the arrayOfAnswerObject with mkResourceFn
 * which then returns updated dataBag
 * @param dataBag - consists of required datas inside it which intiated in SimpleBotService File
 * @returns - returns the updated api Resources dataBag which is required for next processign functions
 */
export const mkResourceWithAnswerObject = (dataBag: any) => {
  let _dataBag = dataBag;
  const { arrayOfAnswerObject } = _dataBag;
  map(
    (resourceObject: any) =>
      (_dataBag = mkResourceFn(resourceObject, _dataBag)),
    arrayOfAnswerObject
  );
  return _dataBag;
};

/**
 * mkSaveInApiDataForArray - this method map the save_in_api field for  single is_mapped_with_same_key key
 * @param mappingKey - contains uniq "is_mapped_with_same_key" in save_in_api_field array in intents config
 * @param dataBag - consists of required datas inside it which intiated in SimpleBotService File
 * @returns the databag with updated values
 */
export const mkSaveInApiDataForArray = (mappingKey: any, dataBag: any) => {
  let _dataBag = dataBag;
  const { arrayOfPath: saveInApiFieldForArrayType } = _dataBag;
  map(
    (path: any) =>
    (_dataBag = prepareSaveInAPIFieldForArrayType(
      mappingKey,
      path,
      _dataBag
    )),
    saveInApiFieldForArrayType
  );
  return _dataBag;
};

//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * mkResourceFn - This method attaches the arrayOfAnswerObject in respective places for
 * array type fields in API resource, it will replace the value if the object is already present,if not
 * appends the object
 * @param resourceObject - contains a single answer object mapped from arrayOfAnswerObjects
 * @param dataBag -consists of required datas inside it which intiated in SimpleBotService File
 * @returns returns the updated api Resources dataBag which is required for next processign functions
 */
const mkResourceFn = (resourceObject: any, dataBag: any) => {
  let _dataBag = dataBag;

  let _apiData = {};
  const { _apiResourceData, apiResource } = _dataBag;
  const startMakerProcessFn = () => {
    /**apiData - consists apiResource fro that particular object  */
    _apiData = pathOr({}, [apiResource], _apiResourceData);
    const { appendedNewObject, indexOfResourceObject } = dataFeederForResource(
      resourceObject,
      _apiData
    );
    if (isIndexIsTrue(indexOfResourceObject)) {
      _apiData = compose(
        set(
          lensPath(
            flatten([
              pathOr([], ['path'], resourceObject),
              indexOfResourceObject,
            ])
          ),
          pathOr({}, ['answerObject'], resourceObject)
        )
      )(_apiData);
      _dataBag = compose(
        set(lensPath(['apiData']), _apiData),
        set(lensPath(['_apiResource', apiResource]), _apiData),
        set(lensPath(['_apiResourceData', apiResource]), _apiData)
      )(_dataBag);
      return _apiData;
    }
    _apiData = compose(
      set(lensPath(pathOr([], ['path'], resourceObject)), appendedNewObject)
    )(_apiData);

    _dataBag = compose(
      set(lensPath(['apiData']), _apiData),
      set(lensPath(['_apiResource', apiResource]), _apiData),
      set(lensPath(['_apiResourceData', apiResource]), _apiData)
    )(_dataBag);
    return;
  };
  startMakerProcessFn();

  return _dataBag;
};

/**
 * prepareSaveInAPIFieldForArrayType - this method prepares
 * @param mappingKey - contains uniq "is_mapped_with_same_key" in save_in_api_field array in intents config
 * @param path - single path object from save_in_api_field
 * @param dataBag - consists of required datas inside it which intiated in SimpleBotService File
 * @returns updated new databag which will be used by its next functions,where two constant
 * will be addeded, i.e. arrayOfAnswerObject = [{
 * path:['<path to the ARRAY in API'],
 * answerObject:{"name":"l","value":"yes"},
 * uniqMapKey:"O"
 * }] and answerObject={"name":"l","value":"yes"}
 */
const prepareSaveInAPIFieldForArrayType = (
  mappingKey: any,
  path: any,
  dataBag: any
) => {
  let _dataBag = dataBag;
  let answerObject = pathOr({}, ['answerObject'], _dataBag);
  let arrayOfAnswerObject: any = pathOr({}, ['arrayOfAnswerObject'], _dataBag);
  const processFn = () => {
    if (isMapWithSameKey(path, mappingKey)) {
      const {
        slasKeyFromSplittedMappedFor,
        dropLastAndGetPath,
        indexOfPushedArray,
      } = dataFeederForPath(path, arrayOfAnswerObject);
      /** set the answer in particular location,and then mk object for setting the data in particular path  */
      answerObject = set(
        lensPath([slasKeyFromSplittedMappedFor]),
        checkAndGetAnswerFrmUserInput(dataBag, path),
        answerObject
      );
      _dataBag = set(lensPath(['answerObject']), answerObject, _dataBag);
      const data = {
        path: dropLastAndGetPath,
        answerObject: pathOr({}, ['answerObject'], _dataBag),
        uniqMapKey: pathOr('', ['is_mapped_with_same_key'], path),
      };
      if (isIndexIsTrue(indexOfPushedArray)) {
        arrayOfAnswerObject = set(
          lensPath([indexOfPushedArray]),
          data,
          arrayOfAnswerObject
        );
        _dataBag = set(
          lensPath(['arrayOfAnswerObject']),
          arrayOfAnswerObject,
          _dataBag
        );
        return;
      }
      arrayOfAnswerObject = append(data, arrayOfAnswerObject);
      _dataBag = set(
        lensPath(['arrayOfAnswerObject']),
        arrayOfAnswerObject,
        _dataBag
      );
      return;
    }
  };
  processFn();
  return _dataBag;
};
/**
 * dataFeederForResource - method which strutues the data required for processing of mkResourceFn
 * @param resourceObject - contains a single answer object mapped from arrayOfAnswerObjects
 * @param apiData - api resource data for the processing intent
 * @returns objects consists of data that will be used by mkResourceFn
 */
const dataFeederForResource = (resourceObject: any, apiData: any) => {
  const checkThisPath: any = view(
    lensPath(pathOr([], ['path'], resourceObject)),
    apiData
  );
  const appendedNewObject = append(
    pathOr({}, ['answerObject'], resourceObject),
    checkThisPath
  );
  const indexOfResourceObject = findIndex((apiObject: any) =>
    findIndexOfObejct(apiObject, resourceObject)
  )(checkThisPath);
  return {
    checkThisPath,
    indexOfResourceObject,
    appendedNewObject,
  };
};
/**
 * findIndexOfObejct - methods useful for finding index of object in API array field
 * @param apiObject - consists of API objects for comparing and getting index
 * @param resourceObject - contains a single answer object mapped from arrayOfAnswerObjects
 * @returns index of object that is going to be updated in API field array
 */
const findIndexOfObejct = (apiObject: any, resourceObject: any) => {
  return equals(
    pathOr(
      {},
      [
        'answerObject',
        head(keys(pathOr({}, ['answerObject'], resourceObject))) as any,
      ],
      resourceObject
    ),
    pathOr({}, [head(keys(apiObject)) as any], apiObject)
  );
};
/**
 * dataFeederForPath - method which retuns the required datas for porcessing for save in path type
 * @param path - path of save_in_api_field
 * @param arrayOfAnswerObject - contains array of answer object
 * @returns objects consists of data that will be used by prepareSaveInAPIFieldForArrayType
 */
const dataFeederForPath = (path: any, arrayOfAnswerObject: any) => {
  /**splitMappedFor - splitted mapped for in path  */
  const splitMappedFor = split('/', pathOr('', ['mapped_for'], path));

  /**slasKeyFromSplittedMappedFor - Taking from last value from splitMappedFor */
  const slasKeyFromSplittedMappedFor: any = last(splitMappedFor);
  /** dropLastAndGetPath - we are dropping last from splitMappedFor constant */
  const dropLastAndGetPath = dropLast(1, splitMappedFor);
  const indexOfPushedArray = findIndex(
    propEq('uniqMapKey', pathOr('', ['is_mapped_with_same_key'], path)),
    arrayOfAnswerObject
  );
  return {
    splitMappedFor,
    slasKeyFromSplittedMappedFor,
    dropLastAndGetPath,
    indexOfPushedArray,
  };
};

